/* eslint-disable */
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, {
  Circle,
  Defs,
  LinearGradient,
  Path,
  Stop,
} from 'react-native-svg';
import {launchImageLibrary} from 'react-native-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AuthHeader from '../../components/AuthHeader';
import AuthInput from '../../components/AuthInput';
import PhoneInput, {
  COUNTRIES,
  DEFAULT_COUNTRY,
} from '../../components/PhoneInput';
import PrimaryButton from '../../components/PrimaryButton';
import ArrowRightIcon from '../../assets/icons/arrow-right.svg';
import MailIcon from '../../assets/icons/mail.svg';
import {colors, card, fonts, spacing} from '../../config/theme';
import {useDispatch, useSelector} from 'react-redux';
import {getProfile, updateProfile} from '../../slice/profileSlice';
import {useEffect, useState} from 'react';
import Toast from 'react-native-toast-message';
import {useTranslation} from 'react-i18next';

const AVATAR_SIZE = 96;
const RING = 4;
const BADGE = 30;
// How far the first card rides up over the forest header.
const OVERLAP = 32;

const UserGlyph = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={colors.textSubtle} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <Path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
  </Svg>
);

// Small drawn camera for the "change photo" badge (no emoji).
const CameraGlyph = () => (
  <Svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={colors.onPrimary} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <Path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <Circle cx={12} cy={13} r={3.5} />
  </Svg>
);

const initialsOf = name => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (parts[0][0] + last).toUpperCase();
};

// "31612345678" or "+31612345678"  ->  {country: Netherlands item, national: "612345678"}
// The stored number is country code + number (no '+'), so the country is found
// by matching the longest country code at the start. A shared code like '1'
// prefers the default country if it matches, otherwise the first match.
const splitPhone = full => {
  const digits = String(full || '').replace(/\D/g, '');
  for (let len = 4; len >= 1; len--) {
    const prefix = digits.slice(0, len);
    if (prefix.length < len || digits.length <= len) {
      continue;
    }
    const matches = COUNTRIES.filter(c => c.dial === prefix);
    if (matches.length > 0) {
      const country =
        matches.find(c => c.code === DEFAULT_COUNTRY.code) || matches[0];
      return {country, national: digits.slice(len)};
    }
  }
  // Unknown format: keep everything in the number box, default country.
  return {country: DEFAULT_COUNTRY, national: digits};
};

// The photo chosen on this phone is remembered per user (see handlePickPhoto).
const photoKey = async () => {
  const userData = await AsyncStorage.getItem('Data');
  return `profilePhoto_${JSON.parse(userData)?.userId}`;
};

// On the web the picker returns a blob: URL, which dies on reload, so the
// photo is stored as a data URL there instead.
const persistableUri = async uri => {
  if (Platform.OS !== 'web') {
    return uri;
  }
  const blob = await (await fetch(uri)).blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Gradient ring around a disc with the user's photo, or their initials.
// Tapping it (or the camera badge) opens the phone's gallery.
const Avatar = ({name, photoUri, onPress, label}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    style={styles.avatar}>
    <Svg width={AVATAR_SIZE} height={AVATAR_SIZE} style={StyleSheet.absoluteFill}>
      <Defs>
        <LinearGradient id="editAvatarRing" x1="0" y1="1" x2="1" y2="0">
          <Stop offset="0" stopColor={colors.primary} />
          <Stop offset="1" stopColor={colors.secondary} />
        </LinearGradient>
      </Defs>
      <Circle
        cx={AVATAR_SIZE / 2}
        cy={AVATAR_SIZE / 2}
        r={AVATAR_SIZE / 2}
        fill="url(#editAvatarRing)"
      />
    </Svg>
    <View style={styles.avatarDisc}>
      {photoUri ? (
        <Image source={{uri: photoUri}} style={styles.avatarImage} />
      ) : (
        <Text style={styles.avatarText}>{initialsOf(name)}</Text>
      )}
    </View>
    <View style={styles.cameraBadge}>
      <CameraGlyph />
    </View>
  </Pressable>
);

// Red message shown under a field that failed validation.
const FieldError = ({message}) =>
  message ? (
    <Text
      style={styles.errorText}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite">
      {message}
    </Text>
  ) : null;

const Profile = ({navigation}) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Same split as signup: the country (flag + code) and the number are separate.
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [photoUri, setPhotoUri] = useState(null);
  const [errors, setErrors] = useState({name: '', phone: ''});
  const [rerender, setRerender] = useState(false);
  const {t} = useTranslation();
  const {name: savedName, updateProfileLoading} = useSelector(
    state => state.profile,
  );

  // Clears one field's error as soon as the user starts fixing it.
  const clearError = field =>
    setErrors(prev => (prev[field] ? {...prev, [field]: ''} : prev));

  // The number as signup sends it: country code + number, digits only,
  // no '+' and no leading 0.  e.g. 31612345678
  const nationalNumber = () => phone.replace(/\D/g, '').replace(/^0+/, '');
  const fullPhone = () => `${String(country.dial).replace(/\D/g, '')}${nationalNumber()}`;

  // Returns the error messages for every field; empty strings mean valid.
  // Whitespace-only input counts as empty. The email is read-only, so it is
  // not validated.
  const validate = () => {
    const next = {name: '', phone: ''};

    if (!name.trim()) {
      next.name = t(
        'root.Profile.errors.nameRequired',
        'Full name is required',
      );
    }

    const national = nationalNumber();
    if (!phone.trim()) {
      next.phone = t(
        'root.Profile.errors.phoneRequired',
        'Phone number is required',
      );
    } else if (!/^\d{6,14}$/.test(national)) {
      next.phone = t(
        'root.Profile.errors.phoneInvalid',
        'Enter a valid phone number',
      );
    }

    return next;
  };

  const handleUpdateProfile = async () => {
    const found = validate();
    setErrors(found);
    // Stop here if any field has an error: nothing is sent to the server.
    if (found.name || found.phone) {
      return;
    }

    try {
      const payload = await dispatch(
        updateProfile({
          name: name.trim(),
          email: email.trim(), // unchanged: the email field is read-only
          phone: fullPhone(),
        }),
      ).unwrap();
      console.log('dispatch updateProfile', payload);
      setRerender(prev => !prev);
      Toast.show({
        type: 'success',
        text1: t('root.Toast.Success.ProfileUpdatedSuccess'),
      });
    } catch (err) {
      console.log(err);
      Toast.show({
        type: 'error',
        text1: err || t('root.Toast.Error.FailedToUpdateProfile'),
      });
    }
  };

  // Opens the phone's photo gallery.
  // TODO(API): there is no endpoint for the picture yet. When the backend adds
  // one, upload `asset` here (asset.uri, asset.type, asset.fileName) and keep
  // the returned URL. Until then the photo is only remembered on this phone.
  const handlePickPhoto = async () => {
    try {
      const res = await launchImageLibrary({
        mediaType: 'photo',
        selectionLimit: 1,
        quality: 0.8,
        maxWidth: 1024,
        maxHeight: 1024,
      });
      if (res.didCancel) {
        return; // the user closed the gallery
      }
      if (res.errorCode) {
        Toast.show({
          type: 'error',
          text1:
            res.errorMessage ||
            t('root.Toast.Error.PhotoFailed', 'Could not open your photos'),
        });
        return;
      }
      const asset = res.assets?.[0];
      if (!asset?.uri) {
        return;
      }
      setPhotoUri(asset.uri);
      try {
        await AsyncStorage.setItem(
          await photoKey(),
          await persistableUri(asset.uri),
        );
      } catch (e) {}
    } catch (err) {
      console.log('pick photo error', err);
      Toast.show({
        type: 'error',
        text1: t('root.Toast.Error.PhotoFailed', 'Could not open your photos'),
      });
    }
  };

  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const payload = await dispatch(getProfile()).unwrap();
        console.log('dispatch getProfile', payload);
        setName(payload.name || '');
        setEmail(payload.email || '');
        const parts = splitPhone(payload.phone_number);
        setCountry(parts.country);
        setPhone(parts.national);
      } catch (err) {
        console.log('getProfile error', err);
      }
    };
    getUserProfile();
  }, [dispatch, rerender]);

  // Bring back the photo picked earlier on this phone.
  useEffect(() => {
    (async () => {
      try {
        const saved = await AsyncStorage.getItem(await photoKey());
        if (saved) {
          setPhotoUri(saved);
        }
      } catch (e) {}
    })();
  }, []);

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t('root.Profile.editTitle')}
        onBack={() => navigation.goBack()}
      />
      <KeyboardAvoidingView
        style={styles.body}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* Forest band the first card overlaps. */}
        <View style={styles.band} />
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <View style={[styles.card, styles.avatarCard]}>
            <Avatar
              name={savedName || name}
              photoUri={photoUri}
              onPress={handlePickPhoto}
              label={t('root.Profile.changePhoto', 'Change profile photo')}
            />
            <Text style={styles.name} numberOfLines={1}>
              {savedName || name}
            </Text>
          </View>

          <View style={[styles.card, styles.formCard]}>
            <Text style={styles.sectionLabel}>
              {t('root.Profile.accountInfo')}
            </Text>

            <View style={styles.field}>
              <AuthInput
                label={t('root.Profile.fullName')}
                placeholder={t('root.Profile.fullName')}
                value={name}
                onChangeText={value => {
                  setName(value);
                  clearError('name');
                }}
                autoCapitalize="words"
                textContentType="name"
                variant="filled"
                icon={<UserGlyph />}
              />
              <FieldError message={errors.name} />
            </View>

            {/* Read-only: the email can't be changed. pointerEvents="none"
                makes sure it can't even be tapped. */}
            <View style={styles.field}>
              <View style={styles.readOnly} pointerEvents="none">
                <AuthInput
                  label={t('root.Profile.emailAddress')}
                  placeholder={t('root.Profile.emailAddress')}
                  value={email}
                  editable={false}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  variant="filled"
                  icon={<MailIcon width={20} height={20} color={colors.textSubtle} />}
                />
              </View>
              <Text style={styles.hintText}>
                {t('root.Profile.emailLocked', "Email can't be changed")}
              </Text>
            </View>

            {/* Country code and number are separate, exactly like signup. */}
            <PhoneInput
              label={t('root.Profile.phoneNumber')}
              placeholder={t('root.Profile.phoneNumber')}
              value={phone}
              onChangeText={value => {
                setPhone(value);
                clearError('phone');
              }}
              error={errors.phone}
              country={country}
              onCountryChange={setCountry}
              autoComplete="tel"
              textContentType="telephoneNumber"
            />
          </View>

          <PrimaryButton
            title={t('root.Profile.save')}
            onPress={handleUpdateProfile}
            loading={updateProfileLoading}
            iconRight={
              <ArrowRightIcon width={16} height={16} color={colors.tertiary} />
            }
            style={styles.saveButton}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  body: {
    flex: 1,
  },
  band: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: OVERLAP + spacing.sm,
    backgroundColor: colors.primary,
  },
  content: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xxl,
    gap: spacing.margin,
  },
  card: {
    ...card,
    borderRadius: 24,
    padding: spacing.lg,
  },

  // Avatar
  avatarCard: {
    alignItems: 'center',
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarDisc: {
    width: AVATAR_SIZE - RING * 2,
    height: AVATAR_SIZE - RING * 2,
    borderRadius: (AVATAR_SIZE - RING * 2) / 2,
    borderWidth: 2,
    borderColor: colors.surface,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarText: {
    fontFamily: fonts.bold,
    fontSize: 24,
    letterSpacing: -0.5,
    color: colors.primary,
  },
  cameraBadge: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: BADGE,
    height: BADGE,
    borderRadius: BADGE / 2,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  name: {
    fontFamily: fonts.bold,
    fontSize: 16,
    lineHeight: 22,
    color: colors.textStrong,
    marginTop: spacing.md,
  },

  // Form
  formCard: {
    gap: spacing.margin,
  },
  sectionLabel: {
    fontFamily: fonts.bold,
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.textMuted,
    paddingHorizontal: spacing.xs,
  },
  // One input plus its error message.
  field: {
    gap: spacing.xs,
  },
  // Greyed-out look for the locked email.
  readOnly: {
    opacity: 0.6,
  },
  hintText: {
    fontFamily: fonts.regular,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textMuted,
    paddingHorizontal: spacing.xs,
  },
  errorText: {
    fontFamily: fonts.regular,
    fontSize: 12,
    lineHeight: 16,
    color: colors.error,
    paddingHorizontal: spacing.xs,
  },
  saveButton: {
    marginTop: spacing.sm,
  },
});

export default Profile;